import React, { useEffect, useState } from "react";

import { yupResolver } from "@hookform/resolvers/yup";
import { confirmResetPassword, fetchUserAttributes } from "aws-amplify/auth";
import { Helmet } from "react-helmet";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import * as Yup from "yup";

import { usePartnerInfo, withController } from "@utils";

import { Button, Notification, Text, Title } from "@ui/elements";
import { Input } from "@ui/forms";
import { Stack } from "@ui/layout";

import { toaster, useAuth } from "@components";

import i18n from "../../services/translation/i18n";

type CreateNewPasswordFields = {
  password: string;
  confirmPassword: string;
};

const ControlledInput = withController<CreateNewPasswordFields>()(Input);

export const CreateNewPassword = () => {
  const { t } = useTranslation();
  const formSchema = Yup.object().shape({
    password: Yup.string().required(t("messages.new-password-required")),
    confirmPassword: Yup.string()
      .required(t("messages.confirm-new-password-required"))
      .oneOf([Yup.ref("password")], t("messages.password-mismatch")),
  });
  const { handleSubmit, control } = useForm<CreateNewPasswordFields>({
    resolver: yupResolver(formSchema),
  });
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const authContext = useAuth();
  const partnerInfo = usePartnerInfo();

  useEffect(() => {
    if (authContext.isAuthenticated === true) {
      navigate("/");
    }
  }, [authContext.isAuthenticated]);

  const onSubmit = async (values: CreateNewPasswordFields) => {
    setLoading(true);

    try {
      const username = searchParams.get("username") || "";
      await confirmResetPassword({
        confirmationCode: searchParams.get("confirmationCode") || "",
        newPassword: values.password,
        username,
        options: {
          clientMetadata: {
            locale: i18n.language,
            partnerId: process.env.REACT_APP_PARTNER_ID!,
          },
        },
      });

      const { result, error } = await authContext.login(
        username,
        values.password,
      );

      if (typeof result !== "boolean") {
        const attributes = await fetchUserAttributes();
        toaster.success({
          title: t("messages.welcome-name", { name: attributes.given_name }),
          message: t("messages.new-password-success"),
        });
        return navigate("/");
      }

      if (error) {
        setError(error.message);
      }
    } catch (error) {
      setLoading(false);
      if (error instanceof Error) {
        return setError(error.message);
      }

      setError(String(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Helmet>
        <title>
          {t("reset-your-password")} - {partnerInfo.name}
        </title>
      </Helmet>
      <Stack direction="column" gap="xl">
        <Stack direction="column">
          <Title size={5}>{t("set-your-new-password")}</Title>
          <Text variant="secondary">
            {t("set-your-new-password-instructions")}
          </Text>
          <ControlledInput
            label={t("New Password")}
            type="password"
            name="password"
            control={control}
            autoComplete="new-password"
          />
          <ControlledInput
            label={t("labels.confirm-new-password")}
            type="password"
            name="confirmPassword"
            control={control}
            autoComplete="new-password"
          />
          {error && <Notification color="danger">{error}</Notification>}
        </Stack>

        <Stack direction="column" gap="xs">
          <Button type="submit" isLoading={loading} color="primary">
            {t("labels.update-password")}
          </Button>
          <Link to="/login">
            <Button as="link" isFullwidth variant="text">
              {t("labels.return-to-login")}
            </Button>
          </Link>
        </Stack>
      </Stack>
    </form>
  );
};
